import { CollaborationPlatform, detectCollaborationPlatform, resolveCollaborationReturnUrl } from '@/services/collaboration-login'
import { toast } from '@/services/utils/common'
import {
    authorizeCollaborationBinding,
    dismissCollaborationBinding,
    getCollaborationBindingStatuses
} from './api'
import type { CollaborationBindingStatus } from './types'
import { nextTick, watch, type App } from 'vue'
import { authenticationState } from '@/services/collaboration-login/auth-state'
import { isAuthenticationPage } from '@/services/collaboration-login/bootstrap'
import { getAccessToken, getStoredTenantId } from '@/services/utils/auth'
import { useUserStore } from '@/store/modules/user'

const GUIDE_SESSION_KEY = 'collaboration-binding-guide-session'
let attemptedSession = ''
let prompting = false
let scheduled = false

function currentSession(): string {
    const userId = useUserStore().userInfo?.id
    return userId ? `${getStoredTenantId()}:${userId}` : ''
}

export function resetCollaborationBindingGuide(): void {
    attemptedSession = ''
    try { sessionStorage.removeItem(GUIDE_SESSION_KEY) } catch { /* Storage may be unavailable. */ }
}

/** Page lifecycle hooks also cover business deep links, not just the workspace. */
export function installCollaborationBindingGuide(app: App): void {
    app.mixin({ onReady: scheduleBindingGuide, onShow: scheduleBindingGuide })
    // 页面钩子可能先于认证恢复完成，也可能在 OAuth 页仍未切换时触发。
    watch(() => authenticationState.status, status => {
        if (status === 'ready') scheduleBindingGuide()
    })
    window.addEventListener('hashchange', scheduleBindingGuide)
}

export function scheduleBindingGuide(): void {
    if (scheduled || isAuthenticationPage(location.href)) return
    scheduled = true
    void nextTick().then(() => {
        requestAnimationFrame(() => {
            scheduled = false
            if (document.visibilityState === 'hidden' || isAuthenticationPage(location.href)
                || authenticationState.status !== 'ready' || authenticationState.token !== getAccessToken()) return
            const href = location.href
            const generation = authenticationState.generation
            void promptCollaborationBindingGuide(href, () => location.href === href
                && document.visibilityState !== 'hidden' && !isAuthenticationPage(location.href)
                && authenticationState.generation === generation && authenticationState.status === 'ready')
        })
    })
}

export {
    completeCollaborationBinding,
    getCollaborationBindingStatuses
} from './api'
export type { CollaborationBindingStatus } from './types'

/**
 * 登录成功后按当前企业配置引导用户绑定协同账号。
 *
 * <p>引导只依赖服务端返回的安装记录状态，前端不按平台类型自行判断是否已绑定。</p>
 */
export async function promptCollaborationBindingGuide(returnUrl = '/', canPrompt = () => true): Promise<boolean> {
    const session = currentSession()
    if (!session || prompting || !canPrompt()) return false
    try {
        if (sessionStorage.getItem(GUIDE_SESSION_KEY) === session) return false
    } catch { /* Use the in-memory guard when storage is unavailable. */ }
    if (attemptedSession === session) return false
    prompting = true
    try {
        const response = await getCollaborationBindingStatuses()
        if (currentSession() !== session || !canPrompt()) return false
        attemptedSession = session
        try { sessionStorage.setItem(GUIDE_SESSION_KEY, session) } catch { /* Keep the in-memory guard. */ }
        const candidates = (response.data || []).filter((item) =>
            item.promptRequired && item.promptEnabled && item.bindingAvailable && !item.bound)
        const candidate = selectEnvironmentCandidate(candidates)
        if (!candidate) return false

        const platformName = getPlatformName(candidate.platform)
        const modalResult = await uni.showModal({
            title: `绑定${platformName}账号`,
            content: `企业已启用${candidate.applicationName || platformName}，绑定后可使用企业已开启的协同功能。`,
            confirmText: '立即绑定',
            cancelText: '稍后再说'
        })
        if (currentSession() !== session || !canPrompt()) return false
        if (!modalResult.confirm) {
            await dismissCollaborationBinding(candidate.installationId)
            return false
        }

        const authorizeResponse = await authorizeCollaborationBinding(candidate.installationId, resolveCollaborationReturnUrl(returnUrl))
        if (currentSession() !== session || !canPrompt()) return false
        const authorizeUrl = authorizeResponse.data?.authorizeUrl
        if (!authorizeUrl) {
            toast('协同账号授权地址生成失败，请稍后重试')
            return false
        }
        window.location.href = authorizeUrl
        return true
    } catch (error) {
        // 绑定引导不能阻断用户正常进入系统。
        console.warn('协同账号绑定引导初始化失败')
        return false
    } finally {
        prompting = false
    }
}

/**
 * 仅选择当前 H5 容器所属平台的待绑定应用。
 *
 * <p>企业微信、钉钉、飞书的 OAuth 授权上下文不能互用。普通浏览器或当前容器未配置对应平台时，
 * 不发起绑定提示，避免引导用户绑定到其他平台应用。</p>
 */
function selectEnvironmentCandidate(candidates: CollaborationBindingStatus[]): CollaborationBindingStatus | undefined {
    const environmentPlatform = detectCollaborationPlatform()
    if (!environmentPlatform) return undefined
    return candidates.find((item) => item.platform === environmentPlatform)
}

function getPlatformName(platform: number): string {
    switch (platform) {
        case CollaborationPlatform.DINGTALK:
            return '钉钉'
        case CollaborationPlatform.WXCP:
            return '企业微信'
        case CollaborationPlatform.FEISHU:
            return '飞书'
        default:
            return '协同平台'
    }
}
